import type { ClassMemberGradeLayout, ClassMemberGroupMeta, ClassMemberRow } from './classMembersStorage';
import { classMemberNameMatches, displayClassMemberName } from './classMemberNameDisplay';
import { parseClassRecordMemoTags } from './classRecordMemoHashtags';

/** 정렬된 첫 그룹 id(없으면 빈 문자열) */
export function firstRecordGroupId(layout: ClassMemberGradeLayout | null | undefined): string {
  const groups = layout?.groups ?? [];
  if (!groups.length) return '';
  const sorted = [...groups].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ko') || a.id.localeCompare(b.id)
  );
  return sorted[0]?.id ?? '';
}

/**
 * 멤버 그룹 기본값: preferred(저장값·현재 선택 학급)가 레이아웃에 있으면 그것,
 * 없으면 첫 그룹. 그룹이 없으면 null.
 */
export function resolveDefaultRecordGroupId(
  layout: ClassMemberGradeLayout | null | undefined,
  preferredId?: string | null
): string | null {
  const groups = layout?.groups ?? [];
  if (!groups.length) return null;
  const preferred = (preferredId ?? '').trim();
  if (preferred && groups.some((g) => g.id === preferred)) return preferred;
  return firstRecordGroupId(layout) || null;
}

/** 그룹 id가 없으면 학급 전체(flatMembers). id가 레이아웃에 없으면 학급 전체로 폴백 */
export function membersForRecordGroup(
  layout: ClassMemberGradeLayout | null | undefined,
  groupId: string | null | undefined,
  flatMembers: ClassMemberRow[]
): ClassMemberRow[] {
  const id = (groupId ?? '').trim();
  if (!id) return flatMembers;
  const groups = layout?.groups ?? [];
  if (!groups.some((g) => g.id === id)) return flatMembers;
  return layout?.membersByGroupId[id] ?? [];
}

function sortGroups(groups: ClassMemberGroupMeta[]): ClassMemberGroupMeta[] {
  return [...groups].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ko') || a.id.localeCompare(b.id)
  );
}

/** 멤버가 속한 그룹(레이아웃 기준) — 수업기록 #태그로 그룹 자동 선택에 사용 */
export type MemberWithGroupContext = {
  memberId: string;
  memberName: string;
  groupId: string;
  groupName: string;
};

export function enumerateMembersWithGroupContext(
  layout: ClassMemberGradeLayout | null | undefined
): MemberWithGroupContext[] {
  if (!layout) return [];
  const out: MemberWithGroupContext[] = [];
  for (const g of sortGroups(layout.groups)) {
    const groupName = (g.name ?? '').trim() || g.id;
    for (const m of layout.membersByGroupId[g.id] ?? []) {
      out.push({
        memberId: m.id,
        memberName: displayClassMemberName(m).trim(),
        groupId: g.id,
        groupName,
      });
    }
  }
  return out;
}

/**
 * 수업기록 #태그 본문(keyword)에 맞는 멤버 후보(그룹 포함).
 * `#m:{id}` / `m:{id}` 를 우선하고, 없으면 이름 완전 일치 → includes 후보.
 */
export function matchMembersByClassRecordTagKeyword(
  layout: ClassMemberGradeLayout | null | undefined,
  keyword: string
): MemberWithGroupContext[] {
  let k = (keyword ?? '').trim();
  if (!k || k === '전체') return [];
  if (k.startsWith('#')) k = k.slice(1);
  if (k.endsWith('-') && k.length > 1 && !k.startsWith('m:')) return [];

  const rows = enumerateMembersWithGroupContext(layout);
  let memberId = '';
  if (k.startsWith('m:')) {
    let rest = k.slice(2);
    if (rest.endsWith('-') && rest.length > 1) rest = rest.slice(0, -1);
    memberId = rest;
  }
  let pool: MemberWithGroupContext[];
  if (memberId) {
    pool = rows.filter((r) => r.memberId === memberId);
  } else {
    const memberOf = (r: MemberWithGroupContext): ClassMemberRow | null =>
      (layout?.membersByGroupId[r.groupId] ?? []).find((m) => m.id === r.memberId) ?? null;
    const exact = rows.filter((r) => {
      const m = memberOf(r);
      return m ? classMemberNameMatches(m, k, 'exact') : r.memberName === k;
    });
    pool =
      exact.length > 0
        ? exact
        : rows.filter((r) => {
            const m = memberOf(r);
            return m ? classMemberNameMatches(m, k, 'includes') : r.memberName.includes(k);
          });
  }
  const dedup = new Map<string, MemberWithGroupContext>();
  for (const r of pool) {
    const key = `${r.groupId}\t${r.memberId}`;
    if (!dedup.has(key)) dedup.set(key, r);
  }
  return Array.from(dedup.values());
}

/**
 * 메모의 멤버 태그(#이름 / #m:id)가 가리키는 학급 그룹 id 목록.
 * 저장 group_id 가 비어 있어도 기록 모아보기에서 학급 탭에 매칭할 때 사용.
 */
export function inferClassRecordNoteGroupIds(
  note: string,
  layout: ClassMemberGradeLayout | null | undefined
): string[] {
  if (!layout || !(note ?? '').trim()) return [];
  const groupIds = new Set<string>();
  for (const tag of parseClassRecordMemoTags(note)) {
    if (tag.kind !== 'include' || tag.sealed) continue;
    const keyword = (tag.memberId ?? tag.keyword ?? '').trim();
    if (!keyword || keyword === '전체') continue;
    const hits = matchMembersByClassRecordTagKeyword(layout, keyword);
    if (hits.length === 0) continue;
    const uniqueMemberIds = Array.from(new Set(hits.map((h) => h.memberId)));
    if (uniqueMemberIds.length === 1) {
      for (const h of hits) {
        if (h.memberId === uniqueMemberIds[0]) groupIds.add(h.groupId);
      }
    } else {
      for (const h of hits) groupIds.add(h.groupId);
    }
  }
  return Array.from(groupIds);
}

/** 태그가 하나의 학급만 가리키면 그 id, 아니면 null */
export function inferSingleClassRecordNoteGroupId(
  note: string,
  layout: ClassMemberGradeLayout | null | undefined
): string | null {
  const ids = inferClassRecordNoteGroupIds(note, layout);
  return ids.length === 1 ? ids[0]! : null;
}

/** 기록 모아보기「기록 조회 그룹」필터 */
export function recordMatchesClassRecordGroupFilter(params: {
  selectedFilterId: string;
  allFilterId: string;
  /** @deprecated 기록 조회 그룹「일반」탭 제거됨 — 남겨 두어도 선택되지 않으면 무시 */
  generalFilterId?: string;
  storedGroupId: string | null | undefined;
  note: string;
  layout: ClassMemberGradeLayout | null | undefined;
}): boolean {
  const { selectedFilterId, allFilterId, generalFilterId, storedGroupId, note, layout } = params;
  if (selectedFilterId === allFilterId) return true;
  const stored = (storedGroupId ?? '').trim();
  if (generalFilterId && selectedFilterId === generalFilterId) {
    if (stored) return false;
    return inferClassRecordNoteGroupIds(note, layout).length === 0;
  }
  if (stored) return stored === selectedFilterId;
  return inferClassRecordNoteGroupIds(note, layout).includes(selectedFilterId);
}
